import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiShield as Shield,
  FiAward as Award,
  FiUsers as Users,
  FiMapPin as MapPin,
  FiPhone as Phone,
  FiCheckCircle as CheckCircle,
} from 'react-icons/fi';
import { FaBuilding as Building } from 'react-icons/fa';
import Footer from '../components/Footer';
import { AGENCY_PHONE_DISPLAY } from '../constants/contact';

const AboutPage = () => {
  const navigate = useNavigate();
  const goToQuote = () => navigate('/quote');

  const stats = [
    { value: '30+', label: 'Years of Service' },
    { value: '500+', label: 'Happy Clients' },
    { value: '6+', label: 'Top-Rated Carriers' },
  ];

  const credentials = [
    { icon: Shield, title: 'Licensed & Bonded', desc: 'Fully licensed insurance agency in the State of California' },
    { icon: Award, title: 'A+ Rated', desc: 'Committed to excellence in service and client satisfaction' },
    { icon: Users, title: '500+ Clients', desc: 'Trusted by families and businesses across Southern California' },
  ];

  const values = [
    'Honest, expert guidance — we work for you, not the insurance company',
    'Independent agency — we shop multiple carriers for the best rates',
    'Bilingual service — English and Tagalog support for our community',
    'Personalized coverage — tailored to your specific needs and budget',
  ];

  return (
    <div className="min-h-screen">
      {/* Header */}
      <section className="pt-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
                Mabuhay! Welcome to{' '}
                <span className="text-[#DC143C]">Pinoy General Insurance</span>
              </h1>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                For over 30 years, Pinoy General Insurance Services has been the trusted insurance partner for the Filipino-American community in Southern California. Founded in 1993, we understand your unique needs and work tirelessly to provide honest, expert guidance and competitive rates.
              </p>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                As an independent agency, we are not tied to any single insurance company. This means we can shop around to find you the best coverage at the best price, always putting your interests first.
              </p>
              <button
                onClick={goToQuote}
                className="bg-[#DC143C] text-white px-8 py-4 rounded-lg font-semibold hover:bg-[#B01030] transition-all duration-200 shadow-md hover:shadow-lg transform hover:scale-105"
              >
                Get a Free Quote
              </button>
            </div>
            <div className="lg:pl-12">
              <div className="aspect-w-4 aspect-h-3 bg-gradient-to-br from-gray-100 to-gray-200 rounded-2xl shadow-xl flex items-center justify-center">
                <div className="text-center p-8">
                  <div className="w-20 h-20 bg-[#0038A8] rounded-full flex items-center justify-center mx-auto mb-4">
                    <Building size={32} className="text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Cerritos Office</h3>
                  <p className="text-gray-600">Serving Southern California since 1993</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-3 gap-6 text-center">
            {stats.map((stat, index) => (
              <div key={index} className="p-6">
                <div className="text-4xl font-bold text-[#DC143C] mb-2">{stat.value}</div>
                <div className="text-gray-600">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Credentials */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Why Choose Us</h2>
            <p className="text-lg text-gray-600">Our commitment to excellence and community</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {credentials.map((cred, index) => (
              <div
                key={index}
                className="bg-white p-8 rounded-xl border border-gray-200 hover:shadow-lg transition-all duration-200 text-center"
              >
                <div className="w-16 h-16 bg-gradient-to-br from-[#0038A8] to-[#002d8a] rounded-full flex items-center justify-center mx-auto mb-6">
                  <cred.icon size={28} className="text-white" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{cred.title}</h3>
                <p className="text-gray-600">{cred.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Our Values</h2>
            <p className="text-lg text-gray-600">What drives us every day</p>
          </div>
          <div className="space-y-4">
            {values.map((value, index) => (
              <div key={index} className="flex items-start gap-4 p-4 rounded-lg hover:bg-gray-50 transition-colors">
                <CheckCircle size={24} className="text-[#DC143C] flex-shrink-0 mt-0.5" />
                <p className="text-gray-700 text-lg">{value}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact info */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <div className="bg-white p-8 rounded-xl shadow-lg flex items-start gap-4">
              <div className="w-12 h-12 bg-[#DC143C] rounded-lg flex items-center justify-center flex-shrink-0">
                <MapPin size={24} className="text-white" />
              </div>
              <div>
                <h3 className="font-bold text-gray-900 mb-1">Our Office</h3>
                <p className="text-gray-600">17304 Norwalk Blvd</p>
                <p className="text-gray-600">Cerritos, CA 90703</p>
              </div>
            </div>
            <div className="bg-white p-8 rounded-xl shadow-lg flex items-start gap-4">
              <div className="w-12 h-12 bg-[#0038A8] rounded-lg flex items-center justify-center flex-shrink-0">
                <Phone size={24} className="text-white" />
              </div>
              <div>
                <h3 className="font-bold text-gray-900 mb-1">Phone & Hours</h3>
                <p className="text-gray-600">{AGENCY_PHONE_DISPLAY}</p>
                <p className="text-gray-600 text-sm">Monday - Friday: 9 AM - 6 PM</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutPage;
